import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { ModeId } from '@evi/skins';

import {
  getOrCreateProgram,
  setupQuadBuffer,
  renderFrame,
  createPausableRAF,
  disposeBuffer,
  clearProgramCache,
  releaseCanvasContext,
  type UniformValue,
} from './glContext.js';
import {
  VERTEX_SHADER_SOURCE,
  OBSIDIAN_SHADER_SOURCE,
  NEON_SHADER_SOURCE,
  prependOrbKind,
  ORB_UNIFORMS,
  prependAuroraVariant,
  AURORA_UNIFORMS,
  prependCyberVariant,
  CYBER_UNIFORMS,
  prependClothVariant,
  CLOTH_UNIFORMS,
  type OrbVariant,
  type AuroraVariant,
  type CyberVariant,
  type ClothVariant,
} from './shaders/index.js';

/**
 * Driver Themes ticket — WebGL2 React component for shader-rendered
 * charging animations. Composes Commit 5's shader sources + ORB
 * uniforms with Commit 6's glContext utilities into a single
 * mountable component.
 *
 * Dispatcher integration is a Commit 8 concern. For now the
 * component is reachable as a public export but unused by any
 * production code path. Once Commit 8 lands, the dispatcher
 * branches to this component whenever the resolved Theme entry
 * declares `chargingAnimation: 'shader'`, passing shaderKind +
 * shaderVariant through from the registry alongside the existing
 * ChargingAnimationProps surface.
 *
 * Prop interface mirrors `ChargingAnimationProps` from the
 * dispatcher exactly so the dispatcher can spread its full props
 * object straight in. The dispatcher's existing frame wrapper
 * handles glow + accent/charging overrides via CSS variables; the
 * shader itself reads colour exclusively from `ORB_UNIFORMS` (for
 * orb variants) or hardcoded shader constants (for obsidian +
 * neon). The override props are accepted for prop-interface
 * compatibility but ignored at this layer; preview-with-overrides
 * for shader themes is a Commit 9 consideration.
 *
 * Lifecycle and performance:
 *   - Setup effect runs on mount and on shaderKind/shaderVariant
 *     change. Re-renders driven by other prop changes (powerKw,
 *     kwhDelivered, etc.) flow through the propsRef without
 *     resetting the WebGL state.
 *   - The compiled WebGLProgram lives in glContext's module-level
 *     cache; theme flips reuse the program on flip-back without
 *     recompiling. Quad buffer is per-mount and disposed on
 *     unmount; the cache survives.
 *   - RAF loop pauses via the Page Visibility API (see
 *     createPausableRAF in glContext.ts). Charging sessions can
 *     last hours in backgrounded tabs; the pause is non-
 *     negotiable.
 *   - `active === false` short-circuits the tick to skip drawing
 *     without tearing down the RAF. Theme picker cards toggle
 *     active to dim their preview without remounting.
 *
 * Failure modes:
 *   - WebGL2 unavailable (old browsers, locked-down environments):
 *     the component renders the canvas DOM element but doesn't
 *     paint. Console.error fires once per page lifetime. The
 *     dispatcher in Commit 8 can wrap with an error boundary or
 *     CSS-fallback if WebGL absence becomes a real concern; for
 *     v1 a transparent canvas is the graceful degradation.
 *   - Shader compile/link failure (data bug): glContext logs and
 *     returns null; the component skips setup and renders an
 *     empty canvas.
 *   - SSR: window references are guarded; useEffect doesn't run
 *     on the server so all WebGL setup defers to mount. Canvas
 *     renders unsized on server (DPR defaults to 1, falls back to
 *     320px when window is undefined). Hydration may mismatch on
 *     backing-store size if the client's DPR differs; the PWA is
 *     SPA-only today so this is theoretical. Document and accept.
 */

interface ShaderChargingAnimationProps {
  // ── Existing ChargingAnimation prop shape (mirrored exactly) ──
  themeCode: string;
  mode: ModeId;
  percent: number;
  powerKw?: number;
  active?: boolean;
  size?: number;
  glow?: 'breathing' | 'subtle' | 'none';
  accentOverride?: string;
  chargingOverride?: string;
  // ── New props introduced by this component ────────────────────
  /** Cumulative kWh delivered this session. Drives uKwhScale,
   *  which controls orb radius / droplet count / sun rise depending
   *  on shader. Capped at 60 kWh for [0, 1] uniform range; values
   *  above 60 still map to 1.0 (no further visual growth). */
  kwhDelivered?: number;
  /** Which shader program backs this render. The dispatcher reads
   *  this from the registry's Theme.shaderKind field. */
  shaderKind:
    | 'orb'
    | 'obsidian'
    | 'neon'
    | 'aurora'
    | 'cyber'
    | 'cloth';
  /** Required when `shaderKind === 'orb'` (drives `#define ORB_KIND`
   *  injection + per-variant uniform tuple selection), when
   *  `shaderKind === 'aurora'` (drives `#define AURORA_VARIANT`),
   *  or when `shaderKind === 'cyber'` (drives `#define
   *  CYBER_VARIANT`). Optional on `obsidian` + `neon` — those
   *  shaders are single-program. */
  shaderVariant?:
    | OrbVariant
    | AuroraVariant
    | CyberVariant
    | ClothVariant;
}

/** Sim kinds capped at 30fps (the theme charging sims — the phone is not
 *  necessarily plugged in while the CAR charges). The lightweight legacy
 *  shaders keep running at display refresh. Only cloth remains here —
 *  the marbling/physarum/fluid kinds all moved to the REAL simulations
 *  in SimChargingAnimation, which carries its own identical cap. */
const CAPPED_SIM_KINDS = new Set<string>(['cloth']);
const SIM_FRAME_CAP_MS = 33;

// ── Sizing helpers (SSR-safe) ────────────────────────────────────────

const SSR_FALLBACK_SIZE = 320;
const MOBILE_WIDTH_BREAKPOINT = 768;
const MOBILE_SIZE_CAP = 400;
const DESKTOP_SIZE = 480;
const MAX_DPR = 2;

function computeCssSize(propsSize: number | undefined): number {
  if (propsSize !== undefined) return propsSize;
  if (typeof window === 'undefined') return SSR_FALLBACK_SIZE;
  return window.innerWidth < MOBILE_WIDTH_BREAKPOINT
    ? Math.min(window.innerWidth * 0.6, MOBILE_SIZE_CAP)
    : DESKTOP_SIZE;
}

function computeDpr(): number {
  if (typeof window === 'undefined') return 1;
  return Math.min(window.devicePixelRatio ?? 1, MAX_DPR);
}

// ── Console-noise dedup ──────────────────────────────────────────────

/** Page-lifetime flag so the WebGL2-unavailable error fires once,
 *  not per component instance. The component may mount many times
 *  per page (picker previews + active session), and a noisy
 *  console.error each time would drown out other diagnostics. */
let WEBGL2_UNSUPPORTED_LOGGED = false;

// ── Uniform mapping ──────────────────────────────────────────────────

/** Clamp [min, max] inclusive. Avoids importing a math util for one
 *  call; the body is small enough that inlining reads cleaner. */
function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

const POWER_KW_DIVISOR = 50.0;
const POWER_KW_MIN_INTENSITY = 0.2;
const POWER_KW_MAX_INTENSITY = 1.0;
const KWH_DIVISOR = 60.0;

// ── Swipe-to-spin interaction (Driver Themes interactivity, part 1) ──────
/** Converts the finger's horizontal velocity (CSS px / ms, from the pointer
 *  handlers) into orb spin velocity (rad/s). Raised so a brisk swipe imparts a
 *  fast full-rotation spin (a full 360 or more), not just a small nudge; the
 *  sign follows the swipe direction. Tunable feel constant. */
const SWIPE_VELOCITY_GAIN = 15.0;
/** Exponential decay rate (per second) of the spin velocity back to baseline
 *  (0 extra). ~2.2/s eases the swipe momentum out over ~1.5–2s — slightly
 *  slower than before so the snappier swipe carries a full turn or more before
 *  it settles back to the baseline cadence. Tunable feel constant. */
const SPIN_DECAY_PER_SEC = 2.2;
/** Hard cap on the swipe-imparted spin velocity (rad/s). With the higher gain a
 *  hard flick could otherwise spin absurdly fast; 40 rad/s integrated over the
 *  decay above is ~2.5 turns — a pronounced full-360+ spin without going wild.
 *  A normal swipe lands well under the cap (~1–2 turns). */
const SPIN_MAX_VELOCITY = 40.0;

// ── Tap-to-effect interactions (Driver Themes interactivity, part 2) ─────
/** Decay rate (per second) of the scalar tap impulse, by theme: Cyber snaps
 *  back fast (chaos), Aurora recollects more slowly (scatter). The orb +
 *  obsidian themes don't read the scalar impulse, so Aurora's rate is the
 *  harmless default there. */
const CYBER_TAP_DECAY_PER_SEC = 6.0;
const AURORA_TAP_DECAY_PER_SEC = 2.0;
/** Decay rate (per second) of Dark Electric's tap-chaos impulse → uOrbChaos.
 *  Fast (like Cyber) so the tap reads as a quick disruption that reforms. Only
 *  the dark-electric orb variant uses it; the smoky orbs keep swipe-spin only. */
const ORB_CHAOS_DECAY_PER_SEC = 6.0;
/** Tap-to-grow (Drip): each tap adds DRIP_GROW_PER_TAP to a size impulse that
 *  accumulates with repeated taps up to DRIP_GROW_CAP (normalised 0→1), then
 *  decays back to 0 at DRIP_GROW_DECAY_PER_SEC once tapping stops — so the
 *  sphere grows on tap and shrinks back to its baseline size on stop. The
 *  obsidian shader scales this onto the sphere radius, additive on top of the
 *  uKwhScale charging growth (both coexist). */
const DRIP_GROW_PER_TAP = 0.35;
const DRIP_GROW_CAP = 1.0;
const DRIP_GROW_DECAY_PER_SEC = 1.6;
/** Neon tap-to-rise: on tap the rise TARGET jumps to 1 and the shader-visible
 *  value approaches it quickly (~0.2s ease-up, no teleport), while the target
 *  itself decays slowly — so the sun rises on tap, hangs, and settles back to
 *  its charge-determined height over a couple of seconds. Re-taps re-lift from
 *  wherever the sun currently is. */
const NEON_RISE_APPROACH_PER_SEC = 6.0;
const NEON_RISE_DECAY_PER_SEC = 0.55;
/** Tap detection: a pointer down→up within this distance (CSS px) and time
 *  (ms) is a tap (fires the per-theme effect); more movement is the swipe the
 *  orb themes use. */
const TAP_SLOP_PX = 8;
const TAP_MAX_MS = 400;

/**
 * Picker-context wind-up duration (ms). 4th iteration of the
 * picker-preview slowdown sweep. Full lineage of the wind-up
 * tempo across the CSS + shader cards:
 *
 *   1.8s / 2s / 0.5s          (original)
 *   3.6s / 4s / 1s            (c41a04a — first doubling)
 *   7.2s / 8s / 2s            (previous doubling)
 *   14.4s / 16s / 4s          (this commit)
 *
 * 14.4s is long for a one-shot picker wind-up. If runtime
 * confirmation reveals it STILL feels too quick at this tempo,
 * the next move is converting to a looping wind-up (0 → target
 * → 0 → target → ...) so the animation continuously plays while
 * the picker is open, rather than completing once and freezing
 * at the target value. That would be a structural change to the
 * tick (re-derive `progress` mod 1.0 instead of clamping at 1.0)
 * rather than another duration doubling.
 *
 * Only active when `kwhDelivered` is undefined (picker / preview
 * mode). Real charging sessions pass `kwhDelivered` directly and
 * the session's natural kWh accumulation IS the wind-up — no
 * synthetic animation needed there.
 */
const WINDUP_DURATION_MS = 14400;

// ── Volumetric step conditional (Commit 11 perf tuning) ──────────────

/** Orb composite's volumetric background loop default step count.
 *  Matches the #ifndef VOLUMETRIC_STEPS fallback in the shader
 *  source so the cache key documents what the shader actually
 *  compiled with (rather than what was injected). */
const VOLUMETRIC_STEPS_DEFAULT = 32;
/** Low-end device step count. ~25% GPU savings on the volumetric
 *  pass vs the default 32. The visual delta is subtle (slightly
 *  banded background gradient) and acceptable for the device class
 *  that triggers it (`navigator.hardwareConcurrency < 4`). */
const VOLUMETRIC_STEPS_LOW_END = 24;
/** Conservative default when navigator.hardwareConcurrency is
 *  unavailable (older browsers, locked-down environments). 8 is a
 *  mid-range value; treating absent as "high-end" avoids
 *  unnecessary visual downgrade on capable devices that don't
 *  expose the API. */
const HARDWARE_CONCURRENCY_FALLBACK = 8;
const LOW_END_HARDWARE_THRESHOLD = 4;

/**
 * Resolved once at module load. navigator.hardwareConcurrency is
 * page-lifetime stable (it can't change per-mount), so module-level
 * resolution avoids re-reading the API on every component instance.
 * SSR-safe via the typeof navigator guard.
 */
const VOLUMETRIC_STEPS =
  typeof navigator !== 'undefined'
    && (navigator.hardwareConcurrency ?? HARDWARE_CONCURRENCY_FALLBACK) < LOW_END_HARDWARE_THRESHOLD
    ? VOLUMETRIC_STEPS_LOW_END
    : VOLUMETRIC_STEPS_DEFAULT;

// ── Component ────────────────────────────────────────────────────────

export function ShaderChargingAnimation(
  props: ShaderChargingAnimationProps,
): JSX.Element {
  const { shaderKind, shaderVariant, size } = props;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // propsRef pattern: the RAF tick closure captures this ref so
  // changes to powerKw / kwhDelivered / active / etc. take effect
  // on the next frame without resetting the setup effect. Updated
  // on every render via the no-dep effect below.
  const propsRef = useRef(props);
  useEffect(() => {
    propsRef.current = props;
  });

  // Hotfix (post-Commit 11) — "render once even when inactive" guard.
  // Commit 7's tick short-circuits the draw when `active === false`.
  // Commit 9 then started passing `active={card.active}` on the
  // /themes picker, so non-active cards mount, set up the WebGL
  // program, and skip every single draw → the canvas stays blank
  // (visible as empty circle outlines on the themes page). This
  // ref tracks whether we've drawn at least one frame for the
  // current setup-effect run; the tick draws on first invocation
  // regardless of `active`, then honours the short-circuit. The
  // ref resets in the setup effect below so a shader identity
  // change (themeCode → shaderKind/Variant change at the
  // dispatcher level → effect re-run) re-paints the first frame
  // of the new theme before re-entering the inactive-skip path.
  const hasRenderedOnceRef = useRef(false);

  // Picker-context wind-up state. Mirrors the CSS animation wind-up
  // pattern from DefaultGlowRing / LuceChargingDial (commit
  // c41a04a) for shader-rendered cards in the /themes picker.
  // `isWindupModeRef` flips true when the component mounts without
  // `kwhDelivered` (picker context); false when kwhDelivered is
  // explicitly passed (real charging session). `windupStartRef`
  // records performance.now() at the start of the wind-up so the
  // RAF tick can compute eased progress. Both reset in the setup
  // effect on every shader identity change so a mid-preview theme
  // switch restarts the wind-up fresh on the new theme.
  const windupStartRef = useRef<number | null>(null);
  const isWindupModeRef = useRef(false);

  // ── Interaction state (Driver Themes interactivity, part 1) ──────────
  // Holds the live swipe impulse alongside propsRef. The pointer handlers
  // (mount effect below) set the per-axis spin velocity from the swipe; the
  // RAF tick accumulates each into its own angle (→ the uSpin vec2 uniform) and
  // decays the velocities back to 0 so the orb eases to its baseline cadence.
  // Separate from the props / uKwhScale path, so the baseline motion + the
  // charging-progress growth are untouched. Part 2's tap effects (Drip /
  // Aurora / Cyber) extend this same ref + handlers.
  const interactionRef = useRef({
    dragging: false,
    // Two-axis swipe-to-spin: horizontal swipe → yaw (spinX, around the
    // vertical axis); vertical swipe → pitch (spinY, around the horizontal
    // axis). Each accumulates + decays independently → the uSpin vec2.
    spinVelX: 0,     // yaw velocity (rad/s) from horizontal swipe; decays to 0
    spinAngleX: 0,   // accumulated yaw → uSpin.x (rad)
    spinVelY: 0,     // pitch velocity (rad/s) from vertical swipe; decays to 0
    spinAngleY: 0,   // accumulated pitch → uSpin.y (rad)
    lastX: 0,        // last pointer x (CSS px) during a drag
    lastY: 0,        // last pointer y (CSS px) during a drag
    lastT: 0,        // performance.now() at the last move
    lastTickMs: 0,   // performance.now() at the last tick (for dt)
    // ── Tap detection + tap-effect impulses (part 2) ──
    downX: 0,        // pointer-down x/y/time, for the tap-vs-swipe test
    downY: 0,
    downT: 0,
    moved: false,    // moved past TAP_SLOP → swipe, not tap
    tapImpulse: 0,   // Aurora scatter / Cyber chaos / cloth ripple (0→1, decays per theme)
    dripGrow: 0,     // Drip size impulse → uGrow (accumulates on tap, decays to 0)
    orbChaos: 0,     // Dark Electric tap-chaos → uOrbChaos (0→1, fast decay)
    neonRiseTarget: 0, // Neon sun-rise target (1 on tap, slow decay)
    neonRise: 0,       // smoothed value the shader sees → uTapRise
    // Cloth ripple origin in the shader's centered p-space (Luce: the
    // rings radiate from the touch point, not the canvas center) plus
    // the tap timestamp that drives the expanding wavefront.
    rippleX: 0,
    rippleY: 0,
    rippleAt: -1,
  });

  const cssSize = computeCssSize(size);
  const dpr = computeDpr();
  const backingSize = Math.round(cssSize * dpr);

  // ── Perf/lifecycle guards (charging-sims foundation) ─────────────────
  // WebGL2-unavailable → render a static themed gradient instead of a
  // transparent canvas (the CSS-var tokens are already set app-wide by the
  // active theme, so the fallback needs no uniforms).
  const [webglFailed, setWebglFailed] = useState(false);
  // Context loss/restore: bumping the epoch re-runs the setup effect against
  // the restored context (whose cached programs were purged).
  const [contextEpoch, setContextEpoch] = useState(0);
  // prefers-reduced-motion: render exactly ONE themed frame, then stop
  // drawing (same short-circuit shape as active===false). Live-updating so
  // an OS-level toggle takes effect without a remount.
  const reducedMotionRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotionRef.current = mql.matches;
    const handler = (e: MediaQueryListEvent) => {
      reducedMotionRef.current = e.matches;
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  // Deterministic WebGL context release on TRUE unmount (route leave).
  // Empty-deps so the cleanup fires ONLY on unmount, never on a
  // shaderKind/variant/epoch re-run (which reuses this same canvas +
  // context — losing it there would black the preview out until the
  // next remount). Declared BEFORE the context-loss listener effect so,
  // under React's reverse-order cleanup, those listeners detach FIRST;
  // loseContext() then fires with no webglcontextlost handler attached,
  // so it can't trip the restore path on a component that's going away.
  // See glContext.releaseCanvasContext for the context-cap rationale
  // (this is what keeps the /themes preview grid from starving the
  // Mapbox map's tiles when the driver returns to /map).
  useEffect(() => {
    const canvas = canvasRef.current;
    return () => releaseCanvasContext(canvas);
  }, []);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onLost = (e: Event): void => {
      // preventDefault tells the browser we intend to restore; without it
      // webglcontextrestored never fires.
      e.preventDefault();
    };
    const onRestored = (): void => {
      // The SAME context object comes back usable, but every program compiled
      // against it is dead — purge its cache entry, then re-run setup.
      const gl = canvas.getContext('webgl2');
      if (gl) clearProgramCache(gl);
      setContextEpoch((n) => n + 1);
    };
    canvas.addEventListener('webglcontextlost', onLost);
    canvas.addEventListener('webglcontextrestored', onRestored);
    return () => {
      canvas.removeEventListener('webglcontextlost', onLost);
      canvas.removeEventListener('webglcontextrestored', onRestored);
    };
  }, []);

  // Setup effect. Re-runs only when the shader identity changes
  // (kind or variant). All per-frame data flows through propsRef
  // so the common case of a powerKw / kwhDelivered tick doesn't
  // tear down the WebGL state.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let glOrNull: WebGL2RenderingContext | null = null;
    try {
      glOrNull = canvas.getContext('webgl2');
    } catch {
      glOrNull = null;
    }
    if (!glOrNull) {
      if (!WEBGL2_UNSUPPORTED_LOGGED) {
        // eslint-disable-next-line no-console
        console.error(
          '[ShaderChargingAnimation] WebGL2 unavailable; rendering the static ' +
          'themed-gradient fallback.',
        );
        WEBGL2_UNSUPPORTED_LOGGED = true;
      }
      setWebglFailed(true);
      return;
    }
    const gl = glOrNull;

    // Pick the fragment shader source + cache key from shaderKind.
    // Cache key convention matches glContext.ts's JSDoc:
    //   - orb shaders → 'orb:<variant>:steps-<n>' (each ORB_KIND ×
    //     VOLUMETRIC_STEPS pair gets its own compiled program).
    //   - obsidian / neon → '<kind>' (single-program shaders).
    //   - aurora → 'aurora:<variant>' (each AURORA_VARIANT gets its
    //     own compiled program; source is identical post-injection
    //     today but the cache split is structural for forward-compat
    //     with future per-variant source branching).
    let fragmentSource: string;
    let cacheKey: string;
    if (shaderKind === 'orb') {
      if (!shaderVariant) {
        // Programming error in the dispatcher — orb requires variant.
        // Skip setup rather than compile a broken shader.
        // eslint-disable-next-line no-console
        console.error(
          '[ShaderChargingAnimation] shaderKind=\'orb\' requires shaderVariant',
        );
        return;
      }
      // Narrow shaderVariant from the OrbVariant | AuroraVariant
      // union — when shaderKind === 'orb' the caller must pass an
      // OrbVariant. The runtime type assertion is safe because the
      // theme registry's Theme.shaderVariant field is statically
      // typed against Theme.shaderKind at the registry layer.
      fragmentSource = prependOrbKind(shaderVariant as OrbVariant, VOLUMETRIC_STEPS);
      cacheKey = `orb:${shaderVariant}:steps-${VOLUMETRIC_STEPS}`;
    } else if (shaderKind === 'aurora') {
      if (!shaderVariant) {
        // Same gate as orb — aurora requires variant.
        // eslint-disable-next-line no-console
        console.error(
          '[ShaderChargingAnimation] shaderKind=\'aurora\' requires shaderVariant',
        );
        return;
      }
      fragmentSource = prependAuroraVariant(shaderVariant as AuroraVariant);
      cacheKey = `aurora:${shaderVariant}`;
    } else if (shaderKind === 'cyber') {
      if (!shaderVariant) {
        // Same gate as orb / aurora — cyber requires variant.
        // eslint-disable-next-line no-console
        console.error(
          '[ShaderChargingAnimation] shaderKind=\'cyber\' requires shaderVariant',
        );
        return;
      }
      fragmentSource = prependCyberVariant(shaderVariant as CyberVariant);
      cacheKey = `cyber:${shaderVariant}`;
    } else if (shaderKind === 'cloth') {
      if (!shaderVariant) {
        // Same gate as orb / aurora / cyber — cloth requires variant.
        // eslint-disable-next-line no-console
        console.error(
          '[ShaderChargingAnimation] shaderKind=\'cloth\' requires shaderVariant',
        );
        return;
      }
      fragmentSource = prependClothVariant(shaderVariant as ClothVariant);
      cacheKey = `cloth:${shaderVariant}`;
    } else if (shaderKind === 'obsidian') {
      fragmentSource = OBSIDIAN_SHADER_SOURCE;
      cacheKey = 'obsidian';
    } else {
      // neon
      fragmentSource = NEON_SHADER_SOURCE;
      cacheKey = 'neon';
    }

    const program = getOrCreateProgram(
      gl,
      cacheKey,
      VERTEX_SHADER_SOURCE,
      fragmentSource,
    );
    if (!program) {
      // getOrCreateProgram already logged the compile/link failure
      // with the cache key. Skip the rest of setup.
      return;
    }

    const buffer = setupQuadBuffer(gl, program);
    if (!buffer) {
      // setupQuadBuffer already logged. Without a buffer the draw
      // call would produce no useful output; skip the RAF loop.
      return;
    }

    // Reset the first-frame guard for this setup-effect run. A new
    // shaderKind/shaderVariant means a different program is now
    // bound; the previously-drawn frame on this canvas is stale
    // and the new theme deserves a fresh first paint before the
    // inactive-skip kicks back in.
    hasRenderedOnceRef.current = false;

    // Reset wind-up state. Wind-up mode is on whenever this mount
    // had no `kwhDelivered` prop at setup time — picker / preview
    // surfaces. Mid-preview theme flips re-run this effect and
    // restart the wind-up from 0 on the new theme. Captured from
    // the propsRef snapshot here (rather than from the React prop
    // directly) because the propsRef has the latest value across
    // re-renders; the setup effect only re-runs on shader-
    // identity changes, so without propsRef the flag would read
    // a stale value if kwhDelivered started undefined but later
    // got passed in (theoretical — no real consumer does this
    // today, but the propsRef pattern is consistent across the
    // file).
    isWindupModeRef.current = propsRef.current.kwhDelivered === undefined;
    windupStartRef.current = isWindupModeRef.current ? performance.now() : null;

    const tick = (): void => {
      // active=false → skip the GPU work AFTER the first frame has
      // landed. Cheaper than tearing down the RAF; theme-picker
      // cards toggle active rapidly when the user hovers between
      // previews and we want flips to be instant. The first-frame
      // exemption keeps non-active picker cards from rendering a
      // blank canvas (hotfix for the C7 + C9 composition regression).
      if (propsRef.current.active === false && hasRenderedOnceRef.current) return;
      // Reduced motion: one themed frame lands (so the surface isn't blank),
      // then the tick stops drawing — zero ongoing GPU work, matching the
      // CSS-path components' matchMedia clamps.
      if (reducedMotionRef.current && hasRenderedOnceRef.current) return;

      const nowMs = performance.now();
      const elapsedSec = nowMs / 1000;

      // ── Swipe-to-spin impulse — JS-side physics, decays to baseline ──
      // Accumulate the swipe-set spin velocity into the spin angle (→ uSpin),
      // then decay the velocity back to 0 so the orb spins in the swiped
      // direction and eases back to its baseline cadence. iTime + uKwhScale
      // below are untouched: baseline motion + charging growth keep running.
      const ia = interactionRef.current;
      const spinDt = ia.lastTickMs === 0 ? 0 : Math.min(0.05, (nowMs - ia.lastTickMs) / 1000);
      ia.lastTickMs = nowMs;
      // Integrate + decay each axis independently (same gain / decay / cap as
      // the single-axis spin, now on both yaw and pitch).
      ia.spinAngleX += ia.spinVelX * spinDt;
      ia.spinVelX *= Math.exp(-SPIN_DECAY_PER_SEC * spinDt);
      if (Math.abs(ia.spinVelX) < 1e-4) ia.spinVelX = 0;
      ia.spinAngleY += ia.spinVelY * spinDt;
      ia.spinVelY *= Math.exp(-SPIN_DECAY_PER_SEC * spinDt);
      if (Math.abs(ia.spinVelY) < 1e-4) ia.spinVelY = 0;
      const uSpin = [ia.spinAngleX, ia.spinAngleY] as const;

      // ── Tap impulse physics (part 2) — decay back to baseline ──
      // The scalar tap impulse (Aurora scatter / Cyber chaos) decays at the
      // active theme's rate; the Drip size impulse (uGrow) decays back to 0 once
      // tapping stops, so the sphere shrinks home. Both are separate from uSpin
      // + uKwhScale and 0 at rest, so baseline motion and charging growth are
      // preserved.
      const tapDecay = propsRef.current.shaderKind === 'cyber'
        ? CYBER_TAP_DECAY_PER_SEC
        : AURORA_TAP_DECAY_PER_SEC;
      ia.tapImpulse *= Math.exp(-tapDecay * spinDt);
      if (ia.tapImpulse < 1e-4) ia.tapImpulse = 0;
      ia.dripGrow *= Math.exp(-DRIP_GROW_DECAY_PER_SEC * spinDt);
      if (ia.dripGrow < 1e-4) ia.dripGrow = 0;
      ia.orbChaos *= Math.exp(-ORB_CHAOS_DECAY_PER_SEC * spinDt);
      if (ia.orbChaos < 1e-4) ia.orbChaos = 0;
      // Neon tap-to-rise: the shader-visible value chases the target
      // (fast ease-up, no snap) while the target decays home — sun
      // rises on tap and settles back to the charge-set position.
      ia.neonRiseTarget *= Math.exp(-NEON_RISE_DECAY_PER_SEC * spinDt);
      if (ia.neonRiseTarget < 1e-3) ia.neonRiseTarget = 0;
      ia.neonRise += (ia.neonRiseTarget - ia.neonRise)
        * Math.min(1, NEON_RISE_APPROACH_PER_SEC * spinDt);
      if (ia.neonRise < 1e-3 && ia.neonRiseTarget === 0) ia.neonRise = 0;
      const uScatter = ia.tapImpulse;
      const uChaos = ia.tapImpulse;
      const uGrow = ia.dripGrow;
      const uOrbChaos = ia.orbChaos;
      const uIntensity = clamp(
        (propsRef.current.powerKw ?? 0) / POWER_KW_DIVISOR,
        POWER_KW_MIN_INTENSITY,
        POWER_KW_MAX_INTENSITY,
      );
      // The shader's kWh-driven visuals (orb size on the orb
      // composite, sun position on neon, droplet count on obsidian)
      // gate on `uKwhScale`. Two resolution paths:
      //
      //   1. Real charging session (kwhDelivered explicitly passed):
      //      direct division — `kwhDelivered / KWH_DIVISOR`. The
      //      session's natural kWh accumulation IS the wind-up;
      //      no synthetic animation needed. ChargingPage uses this
      //      path.
      //
      //   2. Picker / preview (kwhDelivered undefined): synthetic
      //      wind-up. uKwhScale animates from 0 to (percent / 100)
      //      over WINDUP_DURATION_MS using ease-out cubic, matching
      //      the CSS animation wind-up convention from
      //      DefaultGlowRing / LuceChargingDial (commit c41a04a).
      //      ThemesPage cards use this path — gives shader cards
      //      the same "0 → target" sweep the CSS cards already
      //      perform, so the whole picker grid animates together
      //      on mount.
      //
      // Ease-out cubic (`1 - (1 - t)^3`) is the same curve the JS
      // rAF count-ups in the CSS animations use — starts visible,
      // settles smoothly into the target. Linear progression would
      // feel mechanical; ease-in would have a delayed start that
      // wouldn't match the CSS cards' immediate-onset wind-up.
      let kwhScaleRaw: number;
      if (isWindupModeRef.current) {
        const startedAt = windupStartRef.current ?? performance.now();
        const elapsed = performance.now() - startedAt;
        const progress = Math.min(1, elapsed / WINDUP_DURATION_MS);
        const eased = 1 - Math.pow(1 - progress, 3);
        const target = (propsRef.current.percent ?? 0) / 100.0;
        kwhScaleRaw = target * eased;
      } else {
        kwhScaleRaw = (propsRef.current.kwhDelivered ?? 0) / KWH_DIVISOR;
      }
      const uKwhScale = clamp(kwhScaleRaw, 0.0, 1.0);

      const uniforms: Record<string, UniformValue> = {
        iTime: elapsedSec,
        iResolution: [gl.canvas.width, gl.canvas.height] as const,
        uIntensity,
        uKwhScale,
        // Swipe-to-spin angles (vec2: yaw from horizontal swipe, pitch from
        // vertical swipe). The orb shaders rotate by them; bindUniforms skips
        // it for the other shaders (their programs lack the location), so
        // passing it universally is free.
        uSpin,
        // Tap-effect impulses (part 2). uScatter → Aurora, uChaos → Cyber,
        // uGrow → Drip / obsidian; bindUniforms skips each for the shaders that
        // don't declare it, so passing all three universally is free.
        uScatter,
        uChaos,
        uGrow,
        // Dark Electric tap-chaos. Read only in the orb shader's dark-electric
        // branch; the smoky orbs + other shaders leave the location inactive.
        uOrbChaos,
        // Neon tap-to-rise (0 at rest). Only the neon shader declares it;
        // bindUniforms skips the inactive location everywhere else.
        uTapRise: ia.neonRise,
        // Cloth ripple origin + age (Luce): the rings radiate from the
        // last tap point behind an expanding wavefront. Age clamps high
        // when no tap has landed so the wavefront window sits far past
        // the vignette and the ripple term stays visually inert.
        uRippleOrigin: [ia.rippleX, ia.rippleY] as const,
        uRippleAge: ia.rippleAt > 0 ? Math.min(30, (nowMs - ia.rippleAt) / 1000) : 30,
        // Orb shaders consume seven additional colour/shape uniforms
        // per variant; aurora shaders consume five (three light
        // colours + bg base + bg intensity multiplier); obsidian +
        // neon read only iTime / iResolution / uIntensity / uKwhScale
        // and bindUniforms silently skips any names whose locations
        // are inactive in the compiled program (so passing unused
        // uniforms is free).
        ...(shaderKind === 'orb' && shaderVariant
          ? ORB_UNIFORMS[shaderVariant as OrbVariant]
          : {}),
        ...(shaderKind === 'aurora' && shaderVariant
          ? AURORA_UNIFORMS[shaderVariant as AuroraVariant]
          : {}),
        ...(shaderKind === 'cyber' && shaderVariant
          ? CYBER_UNIFORMS[shaderVariant as CyberVariant]
          : {}),
        ...(shaderKind === 'cloth' && shaderVariant
          ? CLOTH_UNIFORMS[shaderVariant as ClothVariant]
          : {}),
      };
      renderFrame(gl, program, uniforms);
      hasRenderedOnceRef.current = true;
    };

    const raf = createPausableRAF(
      tick,
      CAPPED_SIM_KINDS.has(shaderKind) ? { minFrameMs: SIM_FRAME_CAP_MS } : undefined,
    );
    raf.start();

    return () => {
      // Stop the RAF loop and detach the visibility listener (raf.stop
      // is idempotent). Dispose the quad buffer to free GPU memory.
      // Deliberately do NOT call disposeProgram — the program stays
      // in the per-GL-context cache for fast remount on theme flip-
      // back WITHIN the same canvas mount. On a full component
      // unmount (e.g. navigating away from /themes), the canvas
      // gets removed, its gl context gets GC'd, and the WeakMap
      // entry holding this program's per-context inner Map drops
      // automatically — no leak. See glContext.ts PROGRAM_CACHE
      // JSDoc for the per-context cache rationale.
      raf.stop();
      disposeBuffer(gl, buffer);
    };
  }, [shaderKind, shaderVariant, contextEpoch]);

  // ── Pointer gesture capture (Driver Themes interactivity, part 1) ──────
  // Mount-only: the handlers mutate the stable interactionRef, so they don't
  // depend on shaderKind/variant and never re-attach on a theme flip.
  // pointerdown starts a drag + captures the pointer; pointermove sets the
  // spin velocity from the finger's horizontal velocity (sign = swipe
  // direction); up/cancel end the drag, leaving the release momentum for the
  // tick to decay. Shared foundation — part 2's tap effects hang off the same
  // handlers + interactionRef.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ia = interactionRef.current;
    const onDown = (e: PointerEvent): void => {
      ia.dragging = true;
      ia.lastX = e.clientX;
      ia.lastY = e.clientY;
      ia.lastT = performance.now();
      // Tap-detection baseline (part 2).
      ia.downX = e.clientX;
      ia.downY = e.clientY;
      ia.downT = performance.now();
      ia.moved = false;
      try { canvas.setPointerCapture(e.pointerId); } catch { /* capture unsupported — ignore */ }
    };
    const onMove = (e: PointerEvent): void => {
      if (!ia.dragging) return;
      const now = performance.now();
      const dt = now - ia.lastT;
      if (dt <= 0) return;
      const dx = e.clientX - ia.lastX;
      const dy = e.clientY - ia.lastY;
      // Snappy gain, capped so a hard flick can't spin absurdly fast. Both axes
      // share the gain/cap: horizontal motion → yaw, vertical motion → pitch.
      // dy is negated so an upward swipe tips the orb up (screen-y is down-
      // positive); flip this one sign if the up/down direction feels inverted.
      const clampSpin = (v: number): number =>
        Math.max(-SPIN_MAX_VELOCITY, Math.min(SPIN_MAX_VELOCITY, v));
      ia.spinVelX = clampSpin((dx / dt) * SWIPE_VELOCITY_GAIN);
      ia.spinVelY = clampSpin((-dy / dt) * SWIPE_VELOCITY_GAIN);
      ia.lastX = e.clientX;
      ia.lastY = e.clientY;
      ia.lastT = now;
      // Past the slop from the down point → it's a swipe, not a tap.
      if (Math.hypot(e.clientX - ia.downX, e.clientY - ia.downY) > TAP_SLOP_PX) ia.moved = true;
    };
    const onUp = (e: PointerEvent): void => {
      ia.dragging = false;
      try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      // Tap (part 2): a quick down→up with little movement fires the active
      // theme's tap effect. shaderKind is read live via propsRef, so a theme
      // flip is reflected without re-attaching the handlers.
      if (!ia.moved && performance.now() - ia.downT < TAP_MAX_MS) {
        const kind = propsRef.current.shaderKind;
        if (kind === 'obsidian') {
          // Drip: each tap grows the sphere; taps accumulate up to the cap, and
          // the impulse decays back to 0 (shrinks home) once tapping stops.
          ia.dripGrow = Math.min(DRIP_GROW_CAP, ia.dripGrow + DRIP_GROW_PER_TAP);
        } else if (kind === 'orb') {
          // Orb themes spin on swipe. Only Dark Electric -- the smooth sphere
          // that can't show spin -- gets a tap effect: chaos-and-reform. The
          // smoky orbs (Initiative / Electric Light) keep swipe-spin only, so
          // their tap is a no-op here.
          if (propsRef.current.shaderVariant === 'dark-electric') ia.orbChaos = 1;
        } else if (kind === 'neon') {
          // Neon tap-to-rise: the sun lifts toward its full-charge height
          // and eases back to the charge-determined position (see the
          // NEON_RISE_* physics in the tick).
          ia.neonRiseTarget = 1;
        } else {
          // Aurora scatter / Cyber chaos / Luce cloth ripple. The cloth
          // shader also wants WHERE the tap landed: convert the down
          // point into its centered, aspect-corrected p-space
          // (p = (uv - 0.5) * vec2(aspect, 1) * 2, y up) so the rings
          // radiate from the finger. Aurora/cyber ignore the origin.
          const rect = canvas.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            const ux = (ia.downX - rect.left) / rect.width;
            const uy = 1 - (ia.downY - rect.top) / rect.height;
            const aspect = rect.width / rect.height;
            ia.rippleX = (ux - 0.5) * aspect * 2;
            ia.rippleY = (uy - 0.5) * 2;
          }
          ia.rippleAt = performance.now();
          ia.tapImpulse = 1;
        }
      }
    };
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
    };
  }, []);

  if (webglFailed) {
    // Static themed-gradient fallback (WebGL2 unavailable / blocked). Reads
    // the app-wide theme CSS vars — no GL, no animation, zero GPU cost.
    return (
      <div
        aria-hidden
        style={{
          width: `${cssSize}px`,
          height: `${cssSize}px`,
          borderRadius: '50%',
          background:
            'radial-gradient(circle at 50% 45%, var(--evi-charging, #22C55E) 0%, ' +
            'color-mix(in srgb, var(--evi-charging, #22C55E) 30%, transparent) 42%, ' +
            'transparent 72%)',
        }}
      />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      width={backingSize}
      height={backingSize}
      style={{
        width: `${cssSize}px`,
        height: `${cssSize}px`,
        display: 'block',
        // Claim touch gestures so a swipe spins the orb instead of being
        // consumed as a page scroll (same reason the map markers set this).
        touchAction: 'none',
      }}
    />
  );
}
